import './style.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ReactSVG } from 'react-svg';

import SyncIcon from 'assets/images/sync.svg';

import { Label, Text } from '../../../../atoms';
import {
    MeetingItem,
    formatCardDate,
    getMeetingTitle,
    getRecurrenceDescription,
    isOnline,
    isPastMeeting,
    isRecurrent,
    withProtocol,
} from '../../utils';

const useTooltipStyles = makeStyles(() => ({
    tooltip: {
        fontSize: '14px !important',
        fontWeight: 400,
        maxWidth: '600px',
        background: '#1b1b1b',
        borderRadius: 0,
        color: '#fff',
        padding: '4px 9px',
        lineHeight: 1.35,
        cursor: 'pointer',
    },
    tooltipPlacementTop: {
        margin: '0 0 1px !important',
    },
}));

type Props = {
    meeting: MeetingItem;
    highlight?: boolean;
    fallbackTitle?: string;
    onSelect: (meeting: MeetingItem) => void;
};

const useIsTruncated = (value: string) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [truncated, setTruncated] = useState(false);

    const check = useCallback(() => {
        const target = ref.current?.firstElementChild;

        if (!target) {
            return;
        }

        setTruncated(target.scrollWidth > target.clientWidth + 1);
    }, []);

    useEffect(() => {
        check();
        window.addEventListener('resize', check);

        return () => window.removeEventListener('resize', check);
    }, [check, value]);

    return { ref, truncated };
};

const CardMeeting = ({ meeting, highlight = false, fallbackTitle = '', onSelect }: Props) => {
    const tooltipClasses = useTooltipStyles();

    const online = isOnline(meeting);
    const recurrent = isRecurrent(meeting);
    const past = isPastMeeting(meeting);
    const title = getMeetingTitle(meeting, fallbackTitle);
    const address = meeting.address || '';
    const recurrenceDescription = recurrent ? getRecurrenceDescription(meeting) : '';

    const titleState = useIsTruncated(title);
    const addressState = useIsTruncated(address);

    const handleSelect = () => onSelect(meeting);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSelect();
        }
    };

    return (
        <div
            className={`cardInfoData ${highlight ? 'cardInfoDataHighlight' : ''} ${past ? 'cardInfoDataPast' : ''}`}
            role="button"
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
        >
            <div className="cardInfoTop">
                <div className="dateInfo">
                    {recurrent && (
                        <Tooltip
                            title={`Evento recorrente: ${recurrenceDescription}`}
                            placement="top-start"
                            classes={tooltipClasses}
                        >
                            <span className="recurrenceIconWrapper">
                                <ReactSVG src={SyncIcon} className="recurrenceIcon" />
                            </span>
                        </Tooltip>
                    )}
                    <Text component={'h4'} text={formatCardDate(meeting)} />
                </div>

                <div className="badgeGroup">
                    {past && (
                        <Tooltip title="Este evento já ocorreu" placement="top" classes={tooltipClasses}>
                            <span className="doneBadge">Realizado</span>
                        </Tooltip>
                    )}
                    <Label text={meeting.meetingType} />
                </div>
            </div>

            <div className="cardInfoBottom">
                {!!title && (
                    <Tooltip
                        title={title}
                        placement="top-start"
                        classes={tooltipClasses}
                        disableHoverListener={!titleState.truncated}
                    >
                        <span className="meetingTitleWrapper" ref={titleState.ref}>
                            <Text component={'h3'} text={title} className="meetingTitle" />
                        </span>
                    </Tooltip>
                )}

                {online ? (
                    past ? (
                        <span className="meetingAccess meetingAccessDisabled" aria-disabled="true">
                            Entrar na reunião
                        </span>
                    ) : (
                        <a
                            className="meetingAccess"
                            href={withProtocol(address)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                        >
                            Entrar na reunião
                        </a>
                    )
                ) : (
                    !!address && (
                        <Tooltip
                            title={address}
                            placement="top-start"
                            classes={tooltipClasses}
                            disableHoverListener={!addressState.truncated}
                        >
                            <span className="meetingAddressWrapper" ref={addressState.ref}>
                                <Text component={'p'} text={address} className="meetingAddress" />
                            </span>
                        </Tooltip>
                    )
                )}

                {highlight && !!meeting.speaker && (
                    <Text component={'p'} text={meeting.speaker} className="meetingSpeaker" />
                )}
            </div>
        </div>
    );
};

export default CardMeeting;
